import React from "react";
import { Box, LinearProgress, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import useAssessmentStore from "components/Assessment/assessmentStore";

/**
 * Question navigation pills (mobile) — mobile equivalent of
 * `Assessment/QuestionNavigation`.
 *
 * Desktop chunks questions into slides of 10 with arrows + dots; on a phone the
 * strip simply scrolls horizontally and keeps the active pill in view, so no
 * extra controls are needed.
 *
 * Colour states are lifted from desktop's `getState`: untouched = grey outline,
 * analysed OK = green, errored = red, current = solid blue, and an uploading
 * question shows its progress bar and can't be tapped.
 */
export default function QuestionPills() {
  const theme = useTheme();
  const scrollerRef = React.useRef(null);
  const activeRef = React.useRef(null);

  const questions = useAssessmentStore((s) => s.questions);
  const currentQuestion = useAssessmentStore((s) => s.currentQuestion);
  const questionState = useAssessmentStore((s) => s.questionState);
  const questionsError = useAssessmentStore((s) => s.questionsError);
  const uploadStates = useAssessmentStore((s) => s.uploadStates);
  const uploadProgresses = useAssessmentStore((s) => s.uploadProgresses);
  const isInterviewerSpeaking = useAssessmentStore(
    (s) => s.isInterviewerSpeaking
  );
  const setCurrentQuestion = useAssessmentStore((s) => s.setCurrentQuestion);

  // Same lock as desktop: no jumping mid-recording.
  const locked = questionState === "RECORDING" || isInterviewerSpeaking;

  React.useEffect(() => {
    activeRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });
  }, [currentQuestion]);

  const getState = (index) => {
    const error = questionsError[index];
    if (error === undefined) {
      return {
        color: theme.palette.neutral["clr-300"],
        bgColor: theme.palette.shades["clr-white-900"],
      };
    }
    return error
      ? {
          color: theme.palette.danger["clr-700"],
          bgColor: theme.palette.danger["clr-100"],
        }
      : {
          color: theme.palette.success["clr-700"],
          bgColor: theme.palette.success["clr-100"],
        };
  };

  return (
    <Box
      ref={scrollerRef}
      sx={{
        display: "flex",
        gap: 2,
        overflowX: "auto",
        px: 4,
        py: 3,
        bgcolor: "shades.clr-white-900",
        borderRadius: 3,
        border: "1px solid",
        borderColor: "neutral.clr-100",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        "&::-webkit-scrollbar": { display: "none" },
      }}
    >
      {questions.map((question, index) => {
        const selected = index === currentQuestion;
        const uploading = uploadStates[index];
        const progress = uploadProgresses[index];
        const { color, bgColor } = getState(index);

        return (
          <Box
            key={index}
            ref={selected ? activeRef : null}
            component="button"
            type="button"
            disabled={locked || uploading}
            onClick={() => setCurrentQuestion(index)}
            sx={{
              position: "relative",
              flex: "0 0 auto",
              minWidth: 44,
              height: 40,
              px: 2,
              borderRadius: 1.5,
              overflow: "hidden",
              border: "1px solid",
              cursor: locked || uploading ? "not-allowed" : "pointer",
              borderColor: selected
                ? "primary.clr-300"
                : uploading
                ? "neutral.clr-200"
                : color,
              bgcolor: selected ? "primary.clr-300" : uploading ? "shades.clr-white-900" : bgColor,
              color: selected ? theme.palette.shades["clr-white-900"] : color,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "background-color 150ms, border-color 150ms",
            }}
          >
            <Typography variant="body02-bold" color="inherit">
              Q{index + 1}
            </Typography>

            {uploading && (
              <Box sx={{ position: "absolute", bottom: 0, left: 0, width: "100%" }}>
                <LinearProgress
                  variant={progress === 100 ? "indeterminate" : "determinate"}
                  value={progress}
                />
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
